import React, { useState, useEffect } from 'react';
import ShareModal from './modals/ShareModal';
import api from '../config/axiosConfig';
import { API_URLS } from '../config/Constants';
import { sessionManager } from '../config/sessionManager';

interface Game {
  _id: string;
  name?: string;
  opponentName?: string;
  date?: string;
}

interface ShareButtonProps {
  contentType: 'season' | 'game';
  resourceId: string;
  className?: string;
  variant?: 'icon' | 'button' | 'text';
  currentGameId?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({
  contentType,
  resourceId,
  className = '',
  variant = 'button',
  currentGameId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availableGames, setAvailableGames] = useState<Game[]>([]);

  // Check if user has data sharing permission
  // IMPORTANT: This check must be after all hooks to comply with Rules of Hooks
  const canAccessDataSharing = sessionManager.canAccessDataSharing();

  // Fetch games when modal opens and contentType is season
  useEffect(() => {
    if (isModalOpen && contentType === 'season' && availableGames.length === 0) {
      fetchGames();
    }
  }, [isModalOpen, contentType, availableGames.length]);

  const fetchGames = async () => {
    try {
      const clientId = sessionManager.getClientId();
      const seasonId = sessionManager.getSeasonId();

      const response = await api.get(`${API_URLS.BASE_URL}/games?clientId=${clientId}&seasonId=${seasonId}`);

      if (response.data.success) {
        setAvailableGames(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const renderButton = () => {
    switch (variant) {
      case 'icon':
        return (
          <button onClick={handleClick} className={`relative ${className}`} aria-label="Share" title="Share data">
            <img
              src="/icons/season-link.png"
              alt="Share"
              width={20}
              height={20}
              className="w-5 h-5 cursor-pointer transition-opacity duration-200 hover:opacity-75"
            />
          </button>
        );

      case 'text':
        return (
          <button onClick={handleClick} className={`text-blue-600 hover:text-blue-800 underline ${className}`}>
            Share
          </button>
        );

      default: // 'button'
        return (
          <button
            onClick={handleClick}
            className={`bg-sd-navy text-white px-4 py-2 rounded-lg font-semibold hover:bg-sd-navy transition-colors flex items-center gap-2 ${className}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
              />
            </svg>
            Share
          </button>
        );
    }
  };

  // Don't render if user doesn't have permission
  // This check must be AFTER all hooks are called
  if (!canAccessDataSharing) {
    return null;
  }

  return (
    <>
      {renderButton()}

      <ShareModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contentType={contentType}
        resourceId={resourceId}
        availableGames={availableGames}
        currentGameId={currentGameId}
      />
    </>
  );
};

export default ShareButton;
